import {scanMarkdown} from './novaMarkdownScan';
import {classifyToken, NovaIssueKind, NovaValidationContext} from './novaInputValidation';
import {NovaPromptInput, PROMPT_TARGET_TO_INPUT} from './novaVariableLibrary';
import {PromptTarget} from '../state/useNovaPromptState';
import {NovaTestTask, resolveTaskVariableValues} from './novaTaskVariables';
import {capVariableValue, NovaCapUnit} from './novaVariableTruncation';

export const NovaGroundingStatus = {
	Resolved: 'RESOLVED',
	Skipped: 'SKIPPED',
	Truncated: 'TRUNCATED',
	Broken: 'BROKEN',
} as const;
export type NovaGroundingStatus = (typeof NovaGroundingStatus)[keyof typeof NovaGroundingStatus];

export const NovaGroundingReason = {
	// The variable is bound to a field this task leaves empty. Expected and normal, unlike the
	// reasons below, which all point at something the author probably wants to fix.
	NoValue: 'NO_VALUE',
	// The name was a variable once and has since been retired.
	RemovedVariable: 'REMOVED_VARIABLE',
	// The name was never a variable — almost always a typo.
	UnknownName: 'UNKNOWN_NAME',
	// A `{file:…}` whose document is no longer attached to the agent.
	DetachedFile: 'DETACHED_FILE',
	// An attached reference document. It contributes, but its volume is only known once the document
	// is actually read, which happens during the run rather than here.
	AttachedFile: 'ATTACHED_FILE',
} as const;
export type NovaGroundingReason = (typeof NovaGroundingReason)[keyof typeof NovaGroundingReason];

export interface NovaGroundingEntry {
	token: string;
	// Which authored input referenced it — the same token can be fine in one input and dropped in
	// another, so an entry is per (token, input) pair rather than per token.
	input: NovaPromptInput;
	status: NovaGroundingStatus;
	// Present on skipped and broken entries only.
	reason?: NovaGroundingReason;
	// Present on truncated entries only.
	originalSize?: number;
	cappedSize?: number;
	unit?: NovaCapUnit;
}

export interface NovaGroundingBreakdown {
	// Distinct tokens that contributed a value, capped or not.
	sourceCount: number;
	approximateTokens: number;
	// Data that existed but did not fully reach the model.
	withheldCount: number;
	resolved: NovaGroundingEntry[];
	skipped: NovaGroundingEntry[];
	truncated: NovaGroundingEntry[];
	broken: NovaGroundingEntry[];
}

// Mirrors `PromptInputs` from useNovaPromptState — including `agentMd` being a plain string that is
// empty rather than null when the agent has none, so the draft can be handed straight in without a
// null dance at the boundary.
export interface NovaGroundingInputs {
	systemPrompt: string;
	agentMd: string;
	userPromptTemplate: string;
}

const FILE_TOKEN_PREFIX = '{file:';

// Four characters per token is the usual rough English ratio. The estimate is advertised with a "≈"
// and exists to catch an order-of-magnitude problem — a prompt that quietly grew past what is
// sensible — not to bill anyone, so a cheap approximation is the right amount of machinery.
const CHARACTERS_PER_TOKEN = 4;

// `classifyToken` answers BROKEN_REFERENCE for two different situations: a name that was retired from
// the registry, and a `{file:…}` whose document has been detached (it already draws that second case
// by prefix, matching the assembler). The author needs them told apart, because "you removed that
// file" is a different fix from "that variable no longer exists".
function brokenReasonFor(token: string, kind: NovaIssueKind): NovaGroundingReason {
	if (kind === NovaIssueKind.UnknownVariable) {
		return NovaGroundingReason.UnknownName;
	}
	return token.startsWith(FILE_TOKEN_PREFIX) ? NovaGroundingReason.DetachedFile : NovaGroundingReason.RemovedVariable;
}

interface InputUnderTest {
	target: PromptTarget;
	text: string;
}

// Builds the run's grounding breakdown from the agent's saved inputs and the selected task.
//
// This is a *mirror* of what `novaContextAssembly` will do, not its output: it reuses the same
// reference rule (`scanMarkdown`, pinned to the shared vectors) and the same permission rule
// (`classifyToken`), but it substitutes nothing and produces no assembled text. It exists so the
// author gets an honest breakdown before the service can assemble a run for real; once it can, the
// server returns the authoritative breakdown and this whole module is deleted.
//
// Character accounting: a live reference contributes its resolved value's length in place of its own
// literal, and a dropped reference contributes nothing at all — matching the assembler, which removes
// a dropped token entirely rather than leaving a marker. Occurrence length is taken from the
// canonical token, so an author who wrote `{ task_title }` is estimated a couple of characters short;
// that is well inside what "≈" already claims.
export function buildGroundingBreakdown(
	inputs: NovaGroundingInputs,
	task: NovaTestTask,
	context: NovaValidationContext
): NovaGroundingBreakdown {
	const rawValues = resolveTaskVariableValues(task);
	const resolved: NovaGroundingEntry[] = [];
	const skipped: NovaGroundingEntry[] = [];
	const truncated: NovaGroundingEntry[] = [];
	const broken: NovaGroundingEntry[] = [];
	const sourceTokens = new Set<string>();
	const present: InputUnderTest[] = [
		{target: PromptTarget.System, text: inputs.systemPrompt},
		{target: PromptTarget.AgentMd, text: inputs.agentMd},
		{target: PromptTarget.User, text: inputs.userPromptTemplate},
	];

	let totalCharacters = 0;
	for (const {target, text} of present) {
		totalCharacters += text.length;
		const input = PROMPT_TARGET_TO_INPUT[target];
		// One entry per reference, however many times this input mentions it — the same rule
		// `validateInput` applies to authoring feedback. Character accounting still counts every
		// occurrence, because every occurrence is substituted. Kept per input rather than globally so
		// each entry names the input it came from, and so a token that resolves in one input but has
		// no value in another is reported once for each.
		const reported = new Set<string>();
		for (const occurrence of scanMarkdown(text).tokens) {
			const token = occurrence.token;
			// Every reference stops contributing its own literal the moment it is either substituted or
			// dropped, so the literal comes off the total either way.
			totalCharacters -= token.length;

			const isFirstMention = !reported.has(token);
			reported.add(token);

			const issue = classifyToken(token, target, context);
			if (issue !== null) {
				if (isFirstMention) {
					broken.push({
						token,
						input,
						status: NovaGroundingStatus.Broken,
						reason: brokenReasonFor(token, issue.kind),
					});
				}
				continue;
			}

			// An attached reference document is grounding this run, but its text lives in the document
			// rather than in the task, so there is nothing here to measure or cap. Reporting it as
			// having "no value on this task" would state the opposite of what is true — the file is
			// attached and will be read — so it is a source, annotated as read at run time. The token
			// estimate consequently understates a run that reads files; that is what the "≈" is for.
			if (token.startsWith(FILE_TOKEN_PREFIX)) {
				sourceTokens.add(token);
				if (isFirstMention) {
					resolved.push({
						token,
						input,
						status: NovaGroundingStatus.Resolved,
						reason: NovaGroundingReason.AttachedFile,
					});
				}
				continue;
			}

			const rawValue = rawValues[token] ?? '';
			if (rawValue === '') {
				if (isFirstMention) {
					skipped.push({token, input, status: NovaGroundingStatus.Skipped, reason: NovaGroundingReason.NoValue});
				}
				continue;
			}

			const cap = capVariableValue(token, rawValue);
			totalCharacters += cap.value.length;
			sourceTokens.add(token);
			if (cap.wasCapped) {
				if (isFirstMention) {
					truncated.push({
						token,
						input,
						status: NovaGroundingStatus.Truncated,
						originalSize: cap.originalSize,
						cappedSize: cap.cappedSize,
						unit: cap.unit,
					});
				}
				continue;
			}
			if (isFirstMention) {
				resolved.push({token, input, status: NovaGroundingStatus.Resolved});
			}
		}
	}

	// A variable bound to a field this task leaves empty had nothing to withhold, so it is not counted
	// here — reporting "1 withheld" for a client-less task would claim data was held back when none
	// existed. Every other skip reason (not permitted here) is real withholding, as is every cut.
	const withheldCount = truncated.length + skipped.filter(entry => entry.reason !== NovaGroundingReason.NoValue).length;

	return {
		sourceCount: sourceTokens.size,
		approximateTokens: Math.ceil(Math.max(totalCharacters, 0) / CHARACTERS_PER_TOKEN),
		withheldCount,
		resolved,
		skipped,
		truncated,
		broken,
	};
}
